<template>
  <div class="accident-app-shell">
    <section class="accident-hero">
      <div class="hero-glow hero-glow-one"></div>
      <div class="hero-glow hero-glow-two"></div>
      <div class="hero-content">
        <div class="hero-mark" aria-hidden="true">
          <v-icon color="white">mdi-medical-bag</v-icon>
        </div>
        <div class="hero-copy">
          <div class="page-eyebrow">Sicherheit &amp; Gesundheit</div>
          <h1>Unfallmeldungen</h1>
          <p>Unfälle strukturiert erfassen, sicher dokumentieren und gemeinsam nachverfolgen.</p>
        </div>
      </div>
      <v-btn color="primary" depressed class="hero-action" @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        Neue Meldung
      </v-btn>
    </section>

    <section class="overview-grid" aria-label="Übersicht der aktuellen Auswahl">
      <v-card dark flat class="overview-card">
        <div class="overview-icon overview-icon-primary">
          <v-icon small>mdi-format-list-bulleted</v-icon>
        </div>
        <div class="overview-copy">
          <span class="overview-label">Meldungen</span>
          <strong>{{ loading ? "–" : reportCount }}</strong>
          <small>in der aktuellen Auswahl</small>
        </div>
      </v-card>
      <v-card dark flat class="overview-card">
        <div class="overview-icon overview-icon-warning">
          <v-icon small>mdi-briefcase-outline</v-icon>
        </div>
        <div class="overview-copy">
          <span class="overview-label">Betriebsunfälle</span>
          <strong>{{ loading ? "–" : workAccidentCount }}</strong>
          <small>während Arbeit oder Arbeitsweg</small>
        </div>
      </v-card>
      <v-card dark flat class="overview-card">
        <div class="overview-icon overview-icon-neutral">
          <v-icon small>mdi-account-outline</v-icon>
        </div>
        <div class="overview-copy">
          <span class="overview-label">Privatunfälle</span>
          <strong>{{ loading ? "–" : privateAccidentCount }}</strong>
          <small>in der aktuellen Auswahl</small>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="filter-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Ansicht anpassen</div>
          <div class="surface-title-row">
            <h2>Filter</h2>
            <span class="result-count">{{ loading ? "Wird geladen …" : reportCount + " Ergebnisse" }}</span>
          </div>
        </div>
        <v-btn
          text
          class="reset-button"
          :disabled="!hasActiveFilters || loading"
          title="Filter zurücksetzen"
          aria-label="Filter zurücksetzen"
          @click="resetFilters">
          <v-icon left small>mdi-filter-remove-outline</v-icon>
          Zurücksetzen
        </v-btn>
      </div>
      <v-row dense class="filter-fields">
        <v-col v-if="canManage" cols="12" sm="6" md="4">
          <v-select
            v-model="filterStudio"
            :items="studioFilterOptions"
            item-text="name"
            item-value="_id"
            label="Studio"
            prepend-inner-icon="mdi-map-marker-outline"
            outlined
            dense
            dark
            hide-details
            class="modern-field"
            @change="loadReports" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filterStatus"
            :items="statusFilterOptions"
            label="Status"
            prepend-inner-icon="mdi-progress-check"
            outlined
            dense
            dark
            hide-details
            class="modern-field"
            @change="loadReports" />
        </v-col>
      </v-row>
    </v-card>

    <v-card dark flat class="reports-surface">
      <div class="reports-header">
        <div>
          <div class="surface-eyebrow">Aktuelle Auswahl</div>
          <h2>Meldungen</h2>
        </div>
        <div class="reports-legend">
          <span><i class="legend-dot legend-dot-open"></i>Offen</span>
          <span><i class="legend-dot legend-dot-progress"></i>In Bearbeitung</span>
          <span><i class="legend-dot legend-dot-done"></i>Erledigt</span>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.lgAndUp"
        dark
        :headers="tableHeaders"
        :items="reports"
        :loading="loading"
        :items-per-page="25"
        class="transparent reports-table"
        no-data-text="Keine Unfallmeldungen vorhanden"
        loading-text="Unfallmeldungen werden geladen …"
        @click:row="openDetailDialog">
        <template v-slot:item.accidentDate="{ item }">
          <div class="date-cell">
            <strong>{{ $moment(item.accidentDate).format("DD.MM.YYYY") }}</strong>
            <span>{{ $moment(item.accidentDate).format("HH:mm") }} Uhr</span>
          </div>
        </template>
        <template v-slot:item.name="{ item }">
          <div class="person-cell">
            <span class="person-avatar">{{ (item.firstName || "?").charAt(0) }}{{ (item.lastName || "").charAt(0) }}</span>
            <strong>{{ item.firstName }} {{ item.lastName }}</strong>
          </div>
        </template>
        <template v-slot:item.studio="{ item }">
          <span class="studio-cell"><v-icon x-small>mdi-map-marker-outline</v-icon>{{ item.studio ? item.studio.name : "-" }}</span>
        </template>
        <template v-slot:item.isWorkAccident="{ item }">
          <v-chip small dark class="report-chip type-chip" :class="item.isWorkAccident ? 'type-work' : 'type-private'">
            <v-icon left x-small>{{ item.isWorkAccident ? "mdi-briefcase-outline" : "mdi-account-outline" }}</v-icon>
            {{ item.isWorkAccident ? "Betrieb" : "Privat" }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small dark class="report-chip status-chip" :class="'status-' + item.status">
            <span class="chip-dot"></span>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="table-actions">
            <v-btn
              icon
              small
              class="action-button"
              title="PDF herunterladen"
              :aria-label="'PDF für ' + item.firstName + ' ' + item.lastName + ' herunterladen'"
              @click.stop="downloadPdf(item)">
              <v-icon small>mdi-file-pdf-box</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              class="action-button action-button-primary"
              title="Meldung ansehen"
              :aria-label="'Meldung von ' + item.firstName + ' ' + item.lastName + ' ansehen'"
              @click.stop="openDetailDialog(item)">
              <v-icon small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <span class="empty-state-icon"><v-icon>mdi-clipboard-text-search-outline</v-icon></span>
            <strong>Keine Unfallmeldungen gefunden</strong>
            <p>Passe die Filter an oder erfasse eine neue Meldung.</p>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        dark
        :items="reports"
        :loading="loading"
        :items-per-page="25"
        :footer-props="{ itemsPerPageOptions: [25], showFirstLastPage: true }"
        item-key="_id"
        hide-default-header
        no-data-text="Keine Unfallmeldungen vorhanden"
        loading-text="Unfallmeldungen werden geladen …"
        class="reports-mobile-iterator">
        <template v-slot:default="{ items }">
          <div class="mobile-report-list">
            <article
              v-for="item in items"
              :key="item._id"
              class="mobile-report-card"
              tabindex="0"
              role="button"
              :aria-label="'Unfallmeldung von ' + item.firstName + ' ' + item.lastName + ' ansehen'"
              @click="openDetailDialog(item)"
              @keyup.enter.self="openDetailDialog(item)"
              @keydown.space.prevent.self="openDetailDialog(item)">
              <div class="mobile-report-topline">
                <span class="mobile-date">
                  <v-icon x-small>mdi-calendar-blank-outline</v-icon>
                  {{ $moment(item.accidentDate).format("DD.MM.YYYY · HH:mm") }}
                </span>
                <v-chip small dark class="report-chip status-chip" :class="'status-' + item.status">
                  <span class="chip-dot"></span>
                  {{ statusLabel(item.status) }}
                </v-chip>
              </div>
              <div class="mobile-report-person">
                <span class="person-avatar">{{ (item.firstName || "?").charAt(0) }}{{ (item.lastName || "").charAt(0) }}</span>
                <div>
                  <strong>{{ item.firstName }} {{ item.lastName }}</strong>
                  <span><v-icon x-small>mdi-map-marker-outline</v-icon>{{ item.studio ? item.studio.name : "-" }}</span>
                </div>
              </div>
              <div class="mobile-report-footer">
                <v-chip small dark class="report-chip type-chip" :class="item.isWorkAccident ? 'type-work' : 'type-private'">
                  <v-icon left x-small>{{ item.isWorkAccident ? "mdi-briefcase-outline" : "mdi-account-outline" }}</v-icon>
                  {{ item.isWorkAccident ? "Betrieb" : "Privat" }}
                </v-chip>
                <div class="table-actions">
                  <v-btn
                    icon
                    class="action-button"
                    title="PDF herunterladen"
                    :aria-label="'PDF für ' + item.firstName + ' ' + item.lastName + ' herunterladen'"
                    @click.stop="downloadPdf(item)">
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="action-button action-button-primary"
                    title="Meldung ansehen"
                    :aria-label="'Meldung von ' + item.firstName + ' ' + item.lastName + ' ansehen'"
                    @click.stop="openDetailDialog(item)">
                    <v-icon small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </article>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <span class="empty-state-icon"><v-icon>mdi-clipboard-text-search-outline</v-icon></span>
            <strong>Keine Unfallmeldungen gefunden</strong>
            <p>Passe die Filter an oder erfasse eine neue Meldung.</p>
          </div>
        </template>
      </v-data-iterator>
    </v-card>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="createDialog"
      max-width="960"
      persistent
      scrollable
      content-class="modern-report-dialog">
      <v-card dark class="report-dialog">
        <v-card-title class="dialog-header">
          <div class="dialog-mark" aria-hidden="true">
            <v-icon>{{ isEditMode ? "mdi-pencil-outline" : "mdi-plus" }}</v-icon>
          </div>
          <div class="dialog-title-copy">
            <div class="dialog-eyebrow">{{ isEditMode ? "Meldung aktualisieren" : "Neue Meldung erfassen" }}</div>
            <h2>{{ isEditMode ? "Unfallmeldung bearbeiten" : "Unfallmeldung erstellen" }}</h2>
            <p>
              {{
                isEditMode ? "Passe die Angaben an und speichere deine Änderungen." : "Führe alle bekannten Angaben vollständig und sorgfältig auf."
              }}
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon class="dialog-close-button" :disabled="saving" aria-label="Dialog schliessen" @click="closeCreateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="dialog-scroll report-form-scroll">
          <v-form ref="createForm" v-model="formValid" class="report-form pt-4">
            <div class="form-section-heading">
              <span class="section-number">01</span>
              <div>
                <span>Person</span>
                <h3>Persönliche Angaben</h3>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.studio"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Anstellungsort *"
                  outlined
                  dense
                  dark
                  :disabled="!canManage && !isEditMode"
                  :hint="!canManage ? 'Dein angemeldetes Studio' : ''"
                  persistent-hint
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.entryDate" label="Eintrittsdatum" type="date" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.firstName" label="Vorname *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.lastName" label="Nachname *" outlined dense dark :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.address" label="Adresse" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.phone" label="Telefon" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.email" label="E-Mail" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.birthday" label="Geburtstag" type="date" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="section-divider my-6"></v-divider>
            <div class="form-section-heading">
              <span class="section-number">02</span>
              <div>
                <span>Ereignis</span>
                <h3>Angaben zum Unfall</h3>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-switch v-model="form.isWorkAccident" label="Betriebsunfall (während Arbeit oder Arbeitsweg)" dark />
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="form.accidentDate"
                  label="Unfalldatum & Zeit *"
                  type="datetime-local"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>

              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  v-model="zip"
                  label="PLZ *"
                  type="number"
                  placeholder="8500"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld', (v) => (v && v.length >= 4) || 'Mind. 4 Zahlen']"
                  @input="updateAccidentLocation" />
              </v-col>
              <v-col cols="12" sm="8" md="9">
                <v-text-field
                  v-model="city"
                  label="Ortschaft *"
                  placeholder="Frauenfeld"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld']"
                  @input="updateAccidentLocation" />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.accidentDescription"
                  label="Was genau ist passiert? *"
                  outlined
                  dense
                  dark
                  rows="3"
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="form.hasPoliceReport" label="Polizeirapport vorhanden" dark />
              </v-col>
            </v-row>

            <v-divider class="section-divider my-6"></v-divider>
            <div class="form-section-heading">
              <span class="section-number">03</span>
              <div>
                <span>Gesundheit</span>
                <h3>Verletzung</h3>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.injuredBodyPart" label="Was ist verletzt?" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.bodySide"
                  :items="[
                    { text: 'Nicht zutreffend', value: 'n/a' },
                    { text: 'Unbestimmt', value: 'unbestimmt' },
                    { text: 'Links', value: 'links' },
                    { text: 'Rechts', value: 'rechts' },
                    { text: 'Beide', value: 'beide' },
                  ]"
                  label="Körperseite"
                  outlined
                  dense
                  dark />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.injuryType" label="Art der Verletzung" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="section-divider my-6"></v-divider>
            <div class="form-section-heading">
              <span class="section-number">04</span>
              <div>
                <span>Behandlung</span>
                <h3>Ärztliche Behandlung</h3>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.firstDoctor" label="Erstbehandelnder Arzt" outlined dense dark />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.followUpDoctor" label="Nachbehandelnder Arzt" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="section-divider my-6"></v-divider>
            <div class="form-section-heading">
              <span class="section-number">05</span>
              <div>
                <span>Abwesenheit</span>
                <h3>Arbeitsausfall</h3>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-switch v-model="form.absenceMoreThan3Days" label="Arbeit mehr als 3 Tage ausgesetzt" dark />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="form.absenceMoreThan30Days" label="Arbeit voraussichtlich mehr als 30 Tage ausgesetzt" dark />
              </v-col>
            </v-row>
            <v-divider class="section-divider my-6"></v-divider>
            <div class="form-section-heading">
              <span class="section-number">06</span>
              <div>
                <span>Nachweise</span>
                <h3>Arbeitsunfähigkeitszeugnis</h3>
              </div>
            </div>

            <v-alert dense text type="info" color="primary" class="attachment-alert mb-4">
              Bitte lade das Arbeitsunfähigkeitszeugnis direkt hier hoch. Falls dir noch keines vorliegt, sende es nachträglich an
              <strong>ja@wellcomefit.ch</strong>.
            </v-alert>

            <div v-if="isEditMode && existingAttachments.length > 0" class="existing-files mb-4">
              <div class="existing-files-label">Bereits hochgeladen</div>
              <v-card v-for="att in existingAttachments" :key="att.fileId" dark flat class="existing-file-card">
                <span class="file-type-icon"><v-icon small>mdi-paperclip</v-icon></span>
                <span class="file-name text-truncate">{{ att.originalName }}</span>
                <span class="file-size">{{ formatFileSize(att.size) }}</span>
                <v-btn
                  icon
                  small
                  title="Beim Speichern entfernen"
                  aria-label="Anhang beim Speichern entfernen"
                  @click="markAttachmentForRemoval(att.fileId)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </v-card>
            </div>

            <v-file-input
              v-model="form.newAttachments"
              multiple
              accept=".pdf,image/*"
              label="Datei(en) hochladen"
              outlined
              dense
              dark
              show-size
              counter
              prepend-icon=""
              prepend-inner-icon="mdi-file-upload"
              hint="PDF oder Bild (JPG/PNG). Max. 15 MB pro Datei."
              persistent-hint
              class="upload-field" />
          </v-form>
        </v-card-text>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-actions class="dialog-actions form-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text class="secondary-action" @click="closeCreateDialog" :disabled="saving">Abbrechen</v-btn>
          <v-btn color="primary" depressed class="primary-dialog-action" :loading="saving" :disabled="!formValid" @click="saveReport">
            <v-icon left small>{{ isEditMode ? "mdi-content-save-outline" : "mdi-send-outline" }}</v-icon>
            {{ isEditMode ? "Speichern" : "Erfassen & an HR senden" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="detailDialog" max-width="1040" scrollable content-class="modern-report-dialog">
      <v-card dark v-if="currentReport" class="report-dialog detail-dialog">
        <v-card-title class="dialog-header detail-dialog-header">
          <div class="dialog-mark" aria-hidden="true">
            <v-icon>mdi-file-document-outline</v-icon>
          </div>
          <div class="dialog-title-copy">
            <div class="dialog-eyebrow">Unfallmeldung</div>
            <h2>{{ currentReport.firstName }} {{ currentReport.lastName }}</h2>
            <p>{{ formatDateTime(currentReport.accidentDate) }} · {{ currentReport.studio ? currentReport.studio.name : "Kein Studio" }}</p>
          </div>
          <v-spacer></v-spacer>
          <div class="dialog-header-actions">
            <v-chip small dark class="report-chip status-chip detail-status-chip" :class="'status-' + currentReport.status">
              <span class="chip-dot"></span>
              {{ statusLabel(currentReport.status) }}
            </v-chip>
            <v-btn
              icon
              v-if="canEdit(currentReport)"
              class="dialog-icon-action"
              @click="openEditDialog(currentReport)"
              title="Bearbeiten"
              aria-label="Unfallmeldung bearbeiten">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon class="dialog-close-button" aria-label="Dialog schliessen" @click="detailDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-text class="dialog-scroll detail-dialog-body">
          <v-row class="detail-grid">
            <v-col cols="12" md="6" class="d-flex">
              <section class="detail-info-card">
                <div class="detail-section-heading">
                  <span class="detail-section-icon"><v-icon small>mdi-account-outline</v-icon></span>
                  <h4>Persönliche Angaben</h4>
                </div>
                <detail-row label="Studio" :value="currentReport.studio && currentReport.studio.name" />
                <detail-row label="MA" :value="currentReport.firstName + ' ' + currentReport.lastName" />
                <detail-row label="Adresse" :value="currentReport.address" />
                <detail-row label="Telefon" :value="currentReport.phone" />
                <detail-row label="E-Mail" :value="currentReport.email" />
                <detail-row label="Geburtstag" :value="formatDate(currentReport.birthday)" />
                <detail-row label="Eintrittsdatum" :value="formatDate(currentReport.entryDate)" />
              </section>
            </v-col>
            <v-col cols="12" md="6" class="d-flex">
              <section class="detail-info-card">
                <div class="detail-section-heading">
                  <span class="detail-section-icon detail-section-icon-warning"><v-icon small>mdi-alert-circle-outline</v-icon></span>
                  <h4>Unfall</h4>
                </div>
                <detail-row label="Betriebsunfall" :value="currentReport.isWorkAccident ? 'JA' : 'NEIN'" />
                <detail-row label="Datum/Zeit" :value="formatDateTime(currentReport.accidentDate)" />
                <detail-row label="Ort" :value="currentReport.accidentLocation" />
                <detail-row label="Polizeirapport" :value="currentReport.hasPoliceReport ? 'JA' : 'NEIN'" />
                <detail-row label="Verletzung" :value="currentReport.injuredBodyPart" />
                <detail-row label="Körperseite" :value="currentReport.bodySide" />
                <detail-row label="Art" :value="currentReport.injuryType" />
              </section>
            </v-col>
            <v-col cols="12">
              <section class="detail-info-card detail-description-card">
                <div class="detail-section-heading">
                  <span class="detail-section-icon"><v-icon small>mdi-text-box-outline</v-icon></span>
                  <h4>Beschreibung</h4>
                </div>
                <p class="detail-description">{{ currentReport.accidentDescription || "-" }}</p>
              </section>
            </v-col>
            <v-col cols="12" md="6" class="d-flex">
              <section class="detail-info-card">
                <div class="detail-section-heading">
                  <span class="detail-section-icon"><v-icon small>mdi-stethoscope</v-icon></span>
                  <h4>Ärzte</h4>
                </div>
                <detail-row label="Erstbehandlung" :value="currentReport.firstDoctor" />
                <detail-row label="Nachbehandlung" :value="currentReport.followUpDoctor" />
              </section>
            </v-col>
            <v-col cols="12" md="6" class="d-flex">
              <section class="detail-info-card">
                <div class="detail-section-heading">
                  <span class="detail-section-icon"><v-icon small>mdi-calendar-remove-outline</v-icon></span>
                  <h4>Arbeitsausfall</h4>
                </div>
                <detail-row label=">3 Tage" :value="currentReport.absenceMoreThan3Days ? 'JA' : 'NEIN'" />
                <detail-row label=">30 Tage" :value="currentReport.absenceMoreThan30Days ? 'JA' : 'NEIN'" />
              </section>
            </v-col>
          </v-row>

          <div v-if="canManage" class="workflow-card">
            <div class="detail-section-heading">
              <span class="detail-section-icon"><v-icon small>mdi-progress-check</v-icon></span>
              <div>
                <h4>Status verwalten</h4>
                <p>Halte den Bearbeitungsstand für alle Beteiligten aktuell.</p>
              </div>
            </div>
            <v-btn-toggle v-model="currentReport.status" mandatory dense class="status-toggle" @change="changeStatus">
              <v-btn value="open"><span class="toggle-dot toggle-dot-open"></span>Offen</v-btn>
              <v-btn value="in_progress"><span class="toggle-dot toggle-dot-progress"></span>In Bearbeitung</v-btn>
              <v-btn value="done"><span class="toggle-dot toggle-dot-done"></span>Erledigt</v-btn>
            </v-btn-toggle>
          </div>
          <section v-if="currentReport.attachments && currentReport.attachments.length > 0" class="detail-block">
            <div class="detail-block-heading">
              <div>
                <span>Dokumente</span>
                <h4>Anhänge</h4>
              </div>
              <span class="block-count">{{ currentReport.attachments.length }}</span>
            </div>
            <v-card v-for="att in currentReport.attachments" :key="att.fileId" dark flat class="attachment-row">
              <span class="file-type-icon">
                <v-icon small>{{ att.mimeType && att.mimeType.startsWith("image/") ? "mdi-image-outline" : "mdi-file-pdf-box" }}</v-icon>
              </span>
              <div class="attachment-copy">
                <div class="attachment-name text-truncate">{{ att.originalName }}</div>
                <div class="attachment-meta">
                  {{ formatFileSize(att.size) }} ·
                  {{ formatDateTime(att.uploadedAt) }}
                </div>
              </div>
              <div class="attachment-actions">
                <v-btn icon class="action-button" @click="previewAttachment(att)" title="Vorschau" :aria-label="'Vorschau von ' + att.originalName">
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  class="action-button"
                  @click="downloadAttachmentFile(att)"
                  title="Herunterladen"
                  :aria-label="att.originalName + ' herunterladen'">
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
                <v-btn
                  v-if="canEdit(currentReport)"
                  icon
                  class="action-button action-button-danger"
                  @click="deleteAttachmentFile(att)"
                  title="Löschen"
                  :aria-label="att.originalName + ' löschen'">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </v-card>
          </section>

          <section v-if="currentReport.editHistory && currentReport.editHistory.length > 0" class="detail-block">
            <div class="detail-block-heading">
              <div>
                <span>Nachvollziehbarkeit</span>
                <h4>Bearbeitungshistorie</h4>
              </div>
              <span class="block-count">{{ currentReport.editHistory.length }}</span>
            </div>
            <v-expansion-panels dark flat class="history-panels">
              <v-expansion-panel v-for="(h, idx) in [...currentReport.editHistory].reverse()" :key="idx" class="history-panel">
                <v-expansion-panel-header>
                  <div class="history-header">
                    <span class="history-avatar"><v-icon small>mdi-history</v-icon></span>
                    <div class="history-copy">
                      <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                      <span>{{ formatDateTime(h.editedAt) }}</span>
                    </div>
                    <v-chip x-small class="history-count" dark> {{ h.changes ? h.changes.length : 0 }} Änderungen </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(c, cidx) in h.changes" :key="cidx" class="history-change">
                    <strong>{{ c.field }}:</strong>
                    <span class="history-old">{{ formatHistoryValue(c.oldValue, c.field) }}</span>
                    <v-icon x-small>mdi-arrow-right</v-icon>
                    <span class="history-new">{{ formatHistoryValue(c.newValue, c.field) }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>

          <section class="detail-block comments-block">
            <div class="detail-block-heading">
              <div>
                <span>Austausch</span>
                <h4>Kommentare</h4>
              </div>
              <span class="block-count">{{ currentReport.comments ? currentReport.comments.length : 0 }}</span>
            </div>
            <div v-if="currentReport.comments && currentReport.comments.length > 0" class="comment-list">
              <v-card v-for="(c, idx) in currentReport.comments" :key="idx" dark flat class="comment-card">
                <div class="comment-meta">
                  <div class="comment-author">
                    <span class="comment-avatar"><v-icon x-small>mdi-account</v-icon></span>
                    <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                  </div>
                  <span>{{ formatDateTime(c.createdAt) }}</span>
                </div>
                <div class="comment-text">{{ c.comment }}</div>
              </v-card>
            </div>
            <div v-else class="inline-empty-state">
              <v-icon small>mdi-comment-outline</v-icon>
              Noch keine Kommentare.
            </div>

            <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="comment-input" hide-details />
            <div class="comment-submit-row">
              <v-btn color="primary" depressed class="comment-submit" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
                <v-icon left small>mdi-send-outline</v-icon>Kommentar senden
              </v-btn>
            </div>
          </section>
        </v-card-text>
        <v-divider class="dialog-divider"></v-divider>
        <v-card-actions class="dialog-actions detail-dialog-actions">
          <v-btn text color="error" class="delete-action" v-if="canManage" @click="deleteReport">
            <v-icon left>mdi-delete-outline</v-icon>Löschen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined class="pdf-action" @click="downloadPdf(currentReport)"> <v-icon left>mdi-file-pdf-box</v-icon>PDF </v-btn>
          <v-btn color="primary" depressed class="primary-dialog-action" @click="detailDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"

const DetailRow = {
  props: ["label", "value"],
  render(h) {
    return h("div", { class: "detail-row" }, [
      h("span", { class: "detail-row-label" }, this.label + ":"),
      h("span", { class: "detail-row-value" }, this.value || "-"),
    ])
  },
}

export default {
  name: "AccidentReports",
  components: {
    "detail-row": DetailRow,
  },
  data() {
    return {
      userService,
      loading: false,
      saving: false,
      savingComment: false,
      reports: [],
      filterStudio: "all",
      filterStatus: "open",
      editingId: null,
      createDialog: false,
      detailDialog: false,
      formValid: false,
      currentReport: null,
      newComment: "",
      form: this.getEmptyForm(),
      existingAttachments: [],

      // UI-Only Felder
      zip: "",
      city: "",

      tableHeaders: [
        { text: "Datum", value: "accidentDate" },
        { text: "Name", value: "name" },
        { text: "Studio", value: "studio" },
        { text: "Typ", value: "isWorkAccident" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Offen", value: "open" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Erledigt", value: "done" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    isEditMode() {
      return !!this.editingId
    },
    canManage() {
      return this.userService.hasRight("d_manageAccidentProtocols")
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    reportCount() {
      return this.reports.length
    },
    workAccidentCount() {
      return this.reports.filter((report) => report.isWorkAccident).length
    },
    privateAccidentCount() {
      return this.reports.filter((report) => !report.isWorkAccident).length
    },
    hasActiveFilters() {
      return this.filterStatus !== "open" || (this.canManage && this.filterStudio !== "all")
    },
  },
  methods: {
    resetFilters() {
      this.filterStudio = "all"
      this.filterStatus = "open"
      this.loadReports()
    },
    // Hilfsmethode: Schreibt PLZ/Ort live in das Hauptformular
    updateAccidentLocation() {
      this.form.accidentLocation = `${this.zip} ${this.city}`.trim()
    },
    formatFileSize(bytes) {
      if (!bytes) return "-"
      if (bytes < 1024) return bytes + " B"
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
      return (bytes / 1024 / 1024).toFixed(1) + " MB"
    },
    markAttachmentForRemoval(fileId) {
      this.form.removedAttachmentIds.push(fileId)
      this.existingAttachments = this.existingAttachments.filter((a) => a.fileId !== fileId)
    },
    async previewAttachment(att) {
      try {
        const url = await api.accidentReports.previewAttachment(this.currentReport._id, att.fileId)
        window.open(url, "_blank")
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        console.error("Preview fehlgeschlagen:", e)
        alert("Vorschau konnte nicht geladen werden")
      }
    },
    downloadAttachmentFile(att) {
      api.accidentReports.downloadAttachment(this.currentReport._id, att.fileId, att.originalName)
    },
    async deleteAttachmentFile(att) {
      if (!confirm(`Anhang "${att.originalName}" wirklich löschen?`)) return
      try {
        await api.accidentReports.deleteAttachment(this.currentReport._id, att.fileId)
        this.currentReport = await api.accidentReports.fetchOne(this.currentReport._id)
        this.loadReports()
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
        alert("Anhang konnte nicht gelöscht werden")
      }
    },
    formatHistoryValue(val, fieldLabel) {
      if (val === null || val === undefined || val === "") return "-"
      if (typeof val === "boolean") return val ? "JA" : "NEIN"
      if (typeof val === "object" && val !== null) {
        if (val.name) return val.name
        if (val._id) return val._id
        return JSON.stringify(val)
      }
      if (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}/)) {
        const m = this.$moment(val)
        if (m.isValid()) {
          const dateOnlyFields = ["Geburtstag", "Eintrittsdatum"]
          return dateOnlyFields.includes(fieldLabel) ? m.format("DD.MM.YYYY") : m.format("DD.MM.YYYY HH:mm")
        }
      }
      return String(val)
    },
    canEdit(report) {
      if (!report || report.status === "done") return false
      if (this.canManage) return true
      const creatorId = report.createdBy && report.createdBy._id ? report.createdBy._id : report.createdBy
      return creatorId && creatorId.toString() === this.userService.getUserId().toString()
    },
    openEditDialog(report) {
      // PLZ/Ort aus vorhandenem String extrahieren für die UI-Felder
      let extractedZip = ""
      let extractedCity = report.accidentLocation || ""
      if (report.accidentLocation) {
        const match = report.accidentLocation.match(/^(\d{4})\s+(.+)$/)
        if (match) {
          extractedZip = match[1]
          extractedCity = match[2]
        }
      }
      this.zip = extractedZip
      this.city = extractedCity

      this.form = {
        studio: report.studio && report.studio._id ? report.studio._id : report.studio,
        firstName: report.firstName || "",
        lastName: report.lastName || "",
        address: report.address || "",
        phone: report.phone || "",
        email: report.email || "",
        birthday: report.birthday ? this.$moment(report.birthday).format("YYYY-MM-DD") : null,
        entryDate: report.entryDate ? this.$moment(report.entryDate).format("YYYY-MM-DD") : null,
        isWorkAccident: !!report.isWorkAccident,
        accidentDate: report.accidentDate ? this.$moment(report.accidentDate).format("YYYY-MM-DDTHH:mm") : null,
        accidentLocation: report.accidentLocation || "",
        accidentDescription: report.accidentDescription || "",
        hasPoliceReport: !!report.hasPoliceReport,
        injuredBodyPart: report.injuredBodyPart || "",
        bodySide: report.bodySide || "n/a",
        injuryType: report.injuryType || "",
        firstDoctor: report.firstDoctor || "",
        followUpDoctor: report.followUpDoctor || "",
        absenceMoreThan3Days: !!report.absenceMoreThan3Days,
        absenceMoreThan30Days: !!report.absenceMoreThan30Days,
        newAttachments: [],
        removedAttachmentIds: [],
      }
      this.editingId = report._id
      this.existingAttachments = [...(report.attachments || [])]
      this.detailDialog = false
      this.createDialog = true
    },
    getEmptyForm() {
      return {
        studio: this.canManage ? null : this.getSelectedStudioId,
        firstName: "",
        lastName: "",
        address: "",
        phone: "",
        email: "",
        birthday: null,
        entryDate: null,
        isWorkAccident: false,
        accidentDate: null,
        accidentLocation: "",
        accidentDescription: "",
        hasPoliceReport: false,
        injuredBodyPart: "",
        bodySide: "n/a",
        injuryType: "",
        firstDoctor: "",
        followUpDoctor: "",
        absenceMoreThan3Days: false,
        absenceMoreThan30Days: false,
        newAttachments: [],
        removedAttachmentIds: [],
      }
    },
    async loadReports() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") {
          params.studio = this.filterStudio
        }
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.reports = await api.accidentReports.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.form = this.getEmptyForm()
      this.zip = ""
      this.city = ""
      this.editingId = null
      this.createDialog = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.resetValidation()
      })
    },
    closeCreateDialog() {
      this.createDialog = false
      this.editingId = null
    },
    async saveReport() {
      if (!this.$refs.createForm.validate()) return
      this.saving = true
      try {
        const fd = new FormData()
        const fields = [
          "studio",
          "firstName",
          "lastName",
          "address",
          "phone",
          "email",
          "birthday",
          "entryDate",
          "isWorkAccident",
          "accidentDate",
          "accidentLocation",
          "accidentDescription",
          "hasPoliceReport",
          "injuredBodyPart",
          "bodySide",
          "injuryType",
          "firstDoctor",
          "followUpDoctor",
          "absenceMoreThan3Days",
          "absenceMoreThan30Days",
        ]
        fields.forEach((f) => {
          const val = this.form[f]
          if (val !== null && val !== undefined) {
            fd.append(f, val)
          }
        })

        if (this.isEditMode && this.form.removedAttachmentIds.length > 0) {
          fd.append("removedAttachmentIds", this.form.removedAttachmentIds.join(","))
        }

        if (this.form.newAttachments && this.form.newAttachments.length > 0) {
          this.form.newAttachments.forEach((file) => fd.append("attachments", file))
        }

        if (this.isEditMode) {
          const updated = await api.accidentReports.update(this.editingId, fd)
          this.currentReport = updated
        } else {
          await api.accidentReports.create(fd)
        }

        this.createDialog = false
        this.editingId = null
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Speichern:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern der Unfallmeldung"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async openDetailDialog(report) {
      try {
        this.currentReport = await api.accidentReports.fetchOne(report._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    async changeStatus(status) {
      if (!this.currentReport) return
      try {
        const updated = await api.accidentReports.updateStatus(this.currentReport._id, status)
        this.currentReport = updated
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentReport) return
      this.savingComment = true
      try {
        const updated = await api.accidentReports.addComment(this.currentReport._id, this.newComment)
        this.currentReport = updated
        this.newComment = ""
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteReport() {
      if (!this.currentReport) return
      if (!confirm("Unfallmeldung wirklich löschen?")) return
      try {
        await api.accidentReports.delete(this.currentReport._id)
        this.detailDialog = false
        this.loadReports()
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    downloadPdf(report) {
      const fileName = `Unfallmeldung_${report.lastName}_${this.$moment(report.accidentDate).format("YYYY-MM-DD")}.pdf`
      api.accidentReports.downloadPdf(report._id, fileName)
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    statusColor(s) {
      return s === "done" ? "green" : s === "in_progress" ? "orange" : "red"
    },
    statusLabel(s) {
      return s === "done" ? "Erledigt" : s === "in_progress" ? "In Bearbeitung" : "Offen"
    },
  },
  created() {
    this.loadReports()
  },
}
</script>

<style scoped>
.accident-app-shell {
  --surface-main: #1c1c1d;
  --surface-raised: rgba(255, 255, 255, 0.045);
  --surface-soft: rgba(255, 255, 255, 0.035);
  --surface-hover: rgba(255, 255, 255, 0.065);
  --border-subtle: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text-main: #f4f4f5;
  --text-muted: #a3a3a8;
  --text-soft: #d4d4d8;
  --brand-primary: #009640;
  --brand-primary-soft: #58d388;
  max-width: 1480px;
  margin: 0 auto;
  padding: 24px;
  color: var(--text-main);
}

.accident-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  min-height: 196px;
  padding: 34px 38px;
  overflow: hidden;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.08), transparent 48%), rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 24px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  isolation: isolate;
}

.hero-glow {
  position: absolute;
  z-index: -1;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  filter: blur(10px);
  pointer-events: none;
}

.hero-glow-one {
  top: -190px;
  right: 14%;
  background: rgba(0, 150, 64, 0.15);
}

.hero-glow-two {
  bottom: -240px;
  left: 16%;
  background: rgba(255, 255, 255, 0.045);
}

.hero-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
}

.hero-mark,
.dialog-mark {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  background: rgba(0, 150, 64, 0.13);
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 17px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.hero-mark .v-icon {
  color: #58d388 !important;
  font-size: 29px;
}

.hero-copy {
  min-width: 0;
}

.page-eyebrow,
.surface-eyebrow,
.dialog-eyebrow {
  color: var(--brand-primary-soft);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.hero-copy h1 {
  margin: 5px 0 7px;
  color: #fff;
  font-size: clamp(30px, 4vw, 44px);
  font-weight: 760;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.hero-copy p {
  max-width: 660px;
  margin: 0;
  color: #b8b8bc;
  font-size: 15px;
  line-height: 1.6;
}

.hero-action {
  z-index: 1;
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 22px !important;
  border-radius: 14px !important;
  background-color: #009640 !important;
  box-shadow: 0 14px 30px rgba(0, 150, 64, 0.18);
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 16px 0;
}

.overview-card {
  display: flex;
  align-items: center;
  gap: 15px;
  min-width: 0;
  min-height: 108px;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.035) !important;
  border: 1px solid var(--border-subtle);
  border-radius: 20px !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.overview-icon,
.detail-section-icon,
.file-type-icon,
.empty-state-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

.overview-icon {
  width: 43px;
  height: 43px;
  border-radius: 13px;
}

.overview-icon-primary {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.overview-icon-warning {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.14);
}

.overview-icon-neutral {
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.07);
}

.overview-icon .v-icon {
  color: currentColor;
}

.overview-copy {
  display: grid;
  min-width: 0;
}

.overview-copy strong {
  color: #fff;
  font-size: 28px;
  font-weight: 760;
  line-height: 1.12;
  letter-spacing: -0.03em;
}

.overview-label {
  overflow: hidden;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.overview-copy small {
  margin-top: 3px;
  overflow: hidden;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-surface,
.reports-surface {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid var(--border-subtle);
  border-radius: 22px !important;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.filter-surface {
  padding: 20px 22px 14px;
  margin-bottom: 16px;
}

.surface-header,
.reports-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.surface-title-row {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: 3px;
}

.surface-title-row h2,
.reports-header h2 {
  margin: 0;
  color: #f4f4f5;
  font-size: 20px;
  font-weight: 740;
  letter-spacing: -0.02em;
}

.result-count,
.block-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 25px;
  padding: 3px 9px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.reset-button {
  min-height: 44px;
  border-radius: 12px !important;
  color: #d4d4d8 !important;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0;
  text-transform: none;
}

.filter-fields {
  margin-top: 10px;
}

.modern-field ::v-deep .v-input__slot,
.report-form ::v-deep .v-text-field--outlined .v-input__slot,
.comment-input ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.035);
  border-radius: 13px;
}

.modern-field ::v-deep .v-text-field__slot label,
.report-form ::v-deep .v-label,
.comment-input ::v-deep .v-label {
  color: #ababaf;
}

.modern-field ::v-deep fieldset,
.report-form ::v-deep fieldset,
.comment-input ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15);
}

.modern-field ::v-deep .v-input__slot:hover fieldset,
.report-form ::v-deep .v-input__slot:hover fieldset,
.comment-input ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(255, 255, 255, 0.28);
}

.modern-field.v-input--is-focused ::v-deep fieldset,
.report-form ::v-deep .v-input--is-focused fieldset,
.comment-input.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.reports-surface {
  padding: 22px 0 0;
  overflow: hidden;
}

.reports-header {
  padding: 0 22px 17px;
}

.reports-legend {
  display: flex;
  align-items: center;
  gap: 15px;
  color: var(--text-muted);
  font-size: 11px;
}

.reports-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-dot,
.chip-dot,
.toggle-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.legend-dot-open,
.toggle-dot-open {
  background: #fb7185;
  box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.12);
}

.legend-dot-progress,
.toggle-dot-progress {
  background: #fbbf24;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12);
}

.legend-dot-done,
.toggle-dot-done {
  background: #58d388;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.12);
}

.reports-table ::v-deep .v-data-table__wrapper {
  border-top: 1px solid var(--border-subtle);
}

.reports-table ::v-deep table {
  border-collapse: separate;
  border-spacing: 0;
}

.reports-table ::v-deep thead th {
  height: 48px;
  color: #8f8f93 !important;
  background: rgba(0, 0, 0, 0.13);
  border-bottom-color: var(--border-subtle) !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.reports-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 160ms ease;
}

.reports-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.045) !important;
}

.reports-table ::v-deep tbody td {
  height: 72px;
  color: #dddddf;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
  font-size: 13px;
}

.reports-table ::v-deep .v-data-footer,
.reports-mobile-iterator ::v-deep .v-data-footer {
  min-height: 62px;
  color: var(--text-muted);
  background: rgba(0, 0, 0, 0.12);
  border-top: 1px solid var(--border-subtle);
}

.date-cell {
  display: grid;
  gap: 2px;
}

.date-cell strong {
  color: #eeeeef;
  font-size: 13px;
  font-weight: 700;
}

.date-cell span {
  color: var(--text-muted);
  font-size: 11px;
}

.person-cell,
.mobile-report-person {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.person-avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #e4e4e7;
  background: rgba(255, 255, 255, 0.065);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 11px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.person-cell strong {
  overflow: hidden;
  color: #f4f4f5;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-cell {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #b8b8bc;
}

.studio-cell .v-icon,
.mobile-report-person .v-icon,
.mobile-date .v-icon {
  color: #8f8f93;
}

.report-chip {
  height: 27px !important;
  border-radius: 9px !important;
  font-size: 10px !important;
  font-weight: 750;
  letter-spacing: 0.015em;
}

.type-work {
  color: #fcd34d !important;
  background: rgba(245, 158, 11, 0.12) !important;
  border: 1px solid rgba(245, 158, 11, 0.18);
}

.type-private {
  color: #d4d4d8 !important;
  background: rgba(255, 255, 255, 0.055) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.status-chip {
  gap: 7px;
  padding: 0 10px !important;
}

.status-open {
  color: #fda4af !important;
  background: rgba(244, 63, 94, 0.11) !important;
  border: 1px solid rgba(251, 113, 133, 0.17);
}

.status-open .chip-dot {
  background: #fb7185;
}

.status-in_progress {
  color: #fcd34d !important;
  background: rgba(245, 158, 11, 0.11) !important;
  border: 1px solid rgba(251, 191, 36, 0.17);
}

.status-in_progress .chip-dot {
  background: #fbbf24;
}

.status-done {
  color: #66d691 !important;
  background: rgba(0, 150, 64, 0.11) !important;
  border: 1px solid rgba(0, 150, 64, 0.25);
}

.status-done .chip-dot {
  background: #58d388;
}

.table-actions,
.attachment-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
}

.action-button,
.dialog-icon-action,
.dialog-close-button {
  min-width: 44px !important;
  width: 44px !important;
  min-height: 44px !important;
  height: 44px !important;
  color: #b8b8bc !important;
  background: rgba(255, 255, 255, 0.055) !important;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px !important;
  transition: color 160ms ease, background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.action-button-primary {
  color: #62d58e !important;
  background: rgba(0, 150, 64, 0.12) !important;
  border-color: rgba(0, 150, 64, 0.25);
}

.action-button-danger {
  color: #fda4af !important;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 230px;
  padding: 32px 20px;
  color: var(--text-muted);
  text-align: center;
}

.empty-state-icon {
  width: 50px;
  height: 50px;
  margin-bottom: 12px;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 15px;
}

.empty-state-icon .v-icon {
  color: currentColor;
}

.empty-state strong {
  color: #e4e4e7;
  font-size: 14px;
}

.empty-state p {
  margin: 5px 0 0;
  font-size: 12px;
}

.reports-mobile-iterator {
  background: transparent !important;
  border-top: 1px solid var(--border-subtle);
}

.mobile-report-list {
  display: grid;
  gap: 10px;
  padding: 12px;
}

.mobile-report-card {
  padding: 15px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 17px;
  outline: none;
  transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.mobile-report-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.mobile-report-topline,
.mobile-report-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mobile-date {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #a3a3a8;
  font-size: 11px;
  font-weight: 650;
}

.mobile-report-person {
  margin: 17px 0;
}

.mobile-report-person > div {
  display: grid;
  min-width: 0;
}

.mobile-report-person strong {
  overflow: hidden;
  color: #f4f4f5;
  font-size: 15px;
  font-weight: 740;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-report-person span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  overflow: hidden;
  color: #a3a3a8;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-dialog {
  --brand-primary-soft: #58d388;
  color: #f4f4f5;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px !important;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.54);
}

.dialog-header {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  min-height: 100px;
  padding: 20px 24px !important;
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
  white-space: normal;
}

.dialog-mark {
  width: 48px;
  height: 48px;
  border-radius: 14px;
}

.dialog-mark .v-icon {
  color: #58d388;
  font-size: 23px;
}

.dialog-title-copy {
  min-width: 0;
}

.dialog-title-copy h2 {
  margin: 2px 0;
  overflow: hidden;
  color: #fff;
  font-size: 21px;
  font-weight: 750;
  line-height: 1.24;
  letter-spacing: -0.025em;
  text-overflow: ellipsis;
}

.dialog-title-copy p {
  max-width: 570px;
  margin: 0;
  overflow: hidden;
  color: #a3a3a8;
  font-size: 12px;
  font-weight: 450;
  line-height: 1.45;
  text-overflow: ellipsis;
}

.dialog-header-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.detail-status-chip {
  margin-right: 2px;
}

.dialog-divider,
.section-divider {
  border-color: rgba(255, 255, 255, 0.085) !important;
}

.dialog-scroll {
  max-height: 70vh;
  padding: 0 24px 24px !important;
}

.report-form {
  max-width: 100%;
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 2px 0 14px;
}

.section-number {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;
  color: #62d58e;
  background: rgba(0, 150, 64, 0.1);
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 11px;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.06em;
}

.form-section-heading > div {
  display: grid;
}

.form-section-heading > div > span {
  color: #8f8f93;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.form-section-heading h3 {
  margin: 0;
  color: #eeeeef;
  font-size: 17px;
  font-weight: 720;
  line-height: 1.35;
  letter-spacing: -0.015em;
}

.report-form ::v-deep .v-input {
  font-size: 13px;
}

.report-form ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 46px;
}

.report-form ::v-deep .v-input--switch {
  min-height: 52px;
  margin-top: 0;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px;
}

.report-form ::v-deep .v-input--switch .v-messages {
  display: none;
}

.attachment-alert {
  border: 1px solid rgba(0, 150, 64, 0.2) !important;
  border-radius: 14px !important;
  font-size: 12px;
  line-height: 1.55;
}

.existing-files-label {
  margin-bottom: 8px;
  color: #a3a3a8;
  font-size: 11px;
  font-weight: 700;
}

.existing-file-card,
.attachment-row {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  padding: 10px 11px;
  background: rgba(255, 255, 255, 0.035) !important;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px !important;
}

.existing-file-card + .existing-file-card,
.attachment-row + .attachment-row {
  margin-top: 8px;
}

.file-type-icon {
  width: 36px;
  height: 36px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
}

.file-type-icon .v-icon {
  color: currentColor;
}

.file-name,
.attachment-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.file-name,
.attachment-name {
  color: #dddddf;
  font-size: 12px;
  font-weight: 650;
}

.file-size,
.attachment-meta {
  flex: 0 0 auto;
  color: #8f8f93;
  font-size: 10px;
}

.upload-field ::v-deep .v-input__slot {
  min-height: 64px !important;
  background: rgba(0, 150, 64, 0.035) !important;
  border-radius: 14px !important;
}

.upload-field ::v-deep fieldset {
  border-style: dashed;
  border-color: rgba(0, 150, 64, 0.28) !important;
}

.dialog-actions {
  min-height: 72px;
  padding: 13px 24px !important;
  background: rgba(20, 20, 21, 0.92);
  gap: 8px;
}

.dialog-actions .v-btn {
  min-height: 44px;
  margin: 0 !important;
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.primary-dialog-action {
  padding: 0 18px !important;
  box-shadow: 0 10px 24px rgba(0, 150, 64, 0.16);
}

.secondary-action,
.delete-action {
  color: #b8b8bc;
}

.detail-dialog-body {
  padding-top: 20px !important;
}

.detail-grid {
  margin: -6px !important;
}

.detail-grid > .col,
.detail-grid > [class*="col-"] {
  padding: 6px !important;
}

.detail-info-card {
  width: 100%;
  padding: 17px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
}

.detail-section-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.detail-section-heading h4,
.detail-block-heading h4 {
  margin: 0;
  color: #eeeeef;
  font-size: 14px;
  font-weight: 730;
  line-height: 1.35;
}

.detail-section-heading p {
  margin: 2px 0 0;
  color: #929296;
  font-size: 11px;
}

.detail-section-icon {
  width: 32px;
  height: 32px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 9px;
}

.detail-section-icon-warning {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.11);
}

.detail-section-icon .v-icon {
  color: currentColor;
}

.detail-info-card ::v-deep .detail-row {
  display: grid;
  grid-template-columns: minmax(112px, 0.8fr) minmax(0, 1.2fr);
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 12px;
  line-height: 1.45;
}

.detail-info-card ::v-deep .detail-row:last-child {
  border-bottom: 0;
}

.detail-info-card ::v-deep .detail-row-label {
  color: #929296;
}

.detail-info-card ::v-deep .detail-row-value {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #dddddf;
  font-weight: 600;
}

.detail-description {
  margin: 0;
  color: #c7c7cb;
  font-size: 12px;
  line-height: 1.75;
  white-space: pre-wrap;
}

.workflow-card,
.detail-block {
  margin-top: 14px;
  padding: 17px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
}

.workflow-card {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(340px, 1.45fr);
  align-items: center;
  gap: 18px;
}

.workflow-card .detail-section-heading {
  margin: 0;
}

.status-toggle {
  display: flex !important;
  width: 100%;
  padding: 4px;
  background: rgba(0, 0, 0, 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px !important;
}

.status-toggle ::v-deep .v-btn {
  flex: 1 1 0;
  min-width: 0 !important;
  min-height: 44px;
  padding: 0 9px !important;
  color: #a3a3a8 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 9px !important;
  box-shadow: none !important;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.status-toggle ::v-deep .v-btn--active {
  color: #f4f4f5 !important;
  background: rgba(255, 255, 255, 0.09) !important;
}

.status-toggle .toggle-dot {
  margin-right: 7px;
}

.detail-block-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 13px;
}

.detail-block-heading > div {
  display: grid;
}

.detail-block-heading > div > span {
  color: #8f8f93;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.attachment-copy {
  display: grid;
}

.attachment-actions {
  flex: 0 0 auto;
}

.history-panels {
  gap: 8px;
}

.history-panel {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 13px !important;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  padding-right: 8px;
}

.history-avatar,
.comment-avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 9px;
}

.history-avatar .v-icon,
.comment-avatar .v-icon {
  color: currentColor;
}

.history-copy {
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
}

.history-copy strong {
  overflow: hidden;
  color: #dddddf;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-copy span {
  color: #8f8f93;
  font-size: 10px;
}

.history-count {
  flex: 0 0 auto;
  color: #d4d4d8 !important;
  background: rgba(255, 255, 255, 0.06) !important;
  font-size: 9px !important;
}

.history-change {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  color: #a3a3a8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 11px;
}

.history-change:last-child {
  border-bottom: 0;
}

.history-change strong {
  color: #d4d4d8;
}

.history-old {
  color: #fda4af;
}

.history-new {
  color: #66d691;
}

.comment-list {
  display: grid;
  gap: 8px;
}

.comment-card {
  padding: 12px 13px;
  background: rgba(255, 255, 255, 0.035) !important;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 13px !important;
}

.comment-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  color: #8f8f93;
  font-size: 10px;
}

.comment-author {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.comment-author strong {
  overflow: hidden;
  color: #dddddf;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-avatar {
  width: 27px;
  height: 27px;
}

.comment-text {
  color: #c7c7cb;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.inline-empty-state {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 10px 12px;
  color: #929296;
  background: rgba(255, 255, 255, 0.025);
  border: 1px dashed rgba(255, 255, 255, 0.11);
  border-radius: 12px;
  font-size: 11px;
}

.comment-input {
  margin-top: 13px;
}

.comment-submit-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 9px;
}

.comment-submit {
  min-height: 44px;
  border-radius: 12px !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.pdf-action {
  color: #d4d4d8 !important;
  border-color: rgba(255, 255, 255, 0.17) !important;
}

.hero-action:not(.v-btn--disabled),
.primary-dialog-action:not(.v-btn--disabled),
.comment-submit:not(.v-btn--disabled) {
  color: #ffffff !important;
  background-color: #009640 !important;
}

.hero-action:not(.v-btn--disabled):hover,
.primary-dialog-action:not(.v-btn--disabled):hover,
.comment-submit:not(.v-btn--disabled):hover {
  background-color: #00aa49 !important;
}

.hero-action:focus-visible,
.reset-button:focus-visible,
.action-button:focus-visible,
.dialog-icon-action:focus-visible,
.dialog-close-button:focus-visible,
.dialog-actions .v-btn:focus-visible,
.comment-submit:focus-visible {
  outline: 2px solid #58d388;
  outline-offset: 2px;
}

.transparent {
  background: transparent !important;
}

@media (hover: hover) and (pointer: fine) {
  .overview-card:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.18);
    box-shadow: 0 16px 38px rgba(0, 0, 0, 0.18);
  }

  .mobile-report-card:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.055);
    border-color: rgba(255, 255, 255, 0.16);
  }

  .action-button:hover,
  .dialog-icon-action:hover,
  .dialog-close-button:hover {
    color: #f4f4f5 !important;
    background: rgba(255, 255, 255, 0.09) !important;
    border-color: rgba(255, 255, 255, 0.16);
    transform: translateY(-1px);
  }

  .action-button-primary:hover {
    color: #70d99a !important;
    background: rgba(0, 150, 64, 0.17) !important;
    border-color: rgba(0, 150, 64, 0.32);
  }
}

@media (max-width: 959px) {
  .accident-app-shell {
    padding: 18px;
  }

  .accident-hero {
    min-height: 178px;
    padding: 28px;
  }

  .hero-copy p {
    max-width: 520px;
  }

  .reports-legend {
    display: none;
  }

  .workflow-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 599px) {
  .accident-app-shell {
    padding: 12px;
  }

  .accident-hero {
    align-items: stretch;
    min-height: auto;
    padding: 22px 19px 19px;
    border-radius: 20px;
  }

  .hero-content {
    align-items: flex-start;
    gap: 13px;
  }

  .hero-mark {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .hero-mark .v-icon {
    font-size: 23px;
  }

  .hero-copy h1 {
    margin-top: 3px;
    font-size: 27px;
  }

  .hero-copy p {
    margin-top: 9px;
    font-size: 12px;
    line-height: 1.55;
  }

  .page-eyebrow {
    font-size: 9px;
  }

  .hero-action {
    align-self: flex-end;
    min-width: 47px !important;
    width: 47px;
    height: 47px;
    min-height: 47px;
    padding: 0 !important;
    font-size: 0;
    border-radius: 14px !important;
  }

  .hero-action ::v-deep .v-btn__content {
    gap: 0;
  }

  .hero-action .v-icon {
    margin: 0 !important;
    font-size: 23px;
  }

  .overview-grid {
    gap: 7px;
    margin: 10px 0;
  }

  .overview-card {
    display: grid;
    align-content: start;
    gap: 7px;
    min-height: 111px;
    padding: 11px 10px;
    border-radius: 15px !important;
  }

  .overview-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }

  .overview-label {
    font-size: 9px;
    white-space: normal;
  }

  .overview-copy strong {
    font-size: 22px;
  }

  .overview-copy small {
    display: none;
  }

  .filter-surface {
    padding: 17px 14px 10px;
    margin-bottom: 10px;
    border-radius: 18px !important;
  }

  .surface-header {
    align-items: flex-start;
  }

  .surface-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .surface-title-row h2,
  .reports-header h2 {
    font-size: 18px;
  }

  .reset-button {
    min-width: 44px !important;
    width: 44px;
    padding: 0 !important;
    font-size: 0;
  }

  .reset-button .v-icon {
    margin: 0 !important;
  }

  .reports-surface {
    padding-top: 18px;
    border-radius: 18px !important;
  }

  .reports-header {
    padding: 0 15px 14px;
  }

  .mobile-report-topline {
    align-items: flex-start;
  }

  .mobile-report-footer .action-button {
    min-width: 44px !important;
    width: 44px !important;
    min-height: 44px !important;
    height: 44px !important;
  }

  .reports-mobile-iterator ::v-deep .v-data-footer {
    justify-content: center;
    padding: 8px;
  }

  .reports-mobile-iterator ::v-deep .v-data-footer__select {
    display: none;
  }

  .report-dialog {
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header {
    flex-wrap: wrap;
    gap: 11px;
    min-height: 90px;
    padding: 15px !important;
  }

  .dialog-mark {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .dialog-title-copy {
    flex: 1 1 calc(100% - 102px);
  }

  .dialog-title-copy h2 {
    font-size: 18px;
  }

  .dialog-title-copy p {
    font-size: 10px;
    white-space: nowrap;
  }

  .dialog-header > .spacer {
    display: none;
  }

  .detail-dialog-header .dialog-header-actions {
    width: 100%;
    padding-left: 53px;
  }

  .detail-dialog-header .detail-status-chip {
    margin-right: auto;
  }

  .dialog-scroll {
    max-height: none;
    padding: 0 15px 18px !important;
  }

  .report-form-scroll {
    padding-top: 0 !important;
  }

  .form-section-heading {
    margin-bottom: 12px;
  }

  .section-divider {
    margin-top: 18px !important;
    margin-bottom: 18px !important;
  }

  .report-form ::v-deep .v-input--switch {
    min-height: 58px;
  }

  .dialog-actions {
    min-height: 70px;
    padding: 11px 15px !important;
    gap: 7px;
  }

  .form-dialog-actions > .spacer {
    display: none;
  }

  .form-dialog-actions .secondary-action {
    flex: 0 1 36%;
  }

  .form-dialog-actions .primary-dialog-action {
    flex: 1 1 58%;
    min-width: 0;
    padding: 0 10px !important;
    font-size: 11px;
  }

  .detail-dialog-actions {
    flex-wrap: wrap;
  }

  .detail-dialog-actions > .spacer {
    display: none;
  }

  .detail-dialog-actions .delete-action {
    margin-right: auto !important;
  }

  .detail-info-card {
    padding: 15px;
  }

  .detail-info-card ::v-deep .detail-row {
    grid-template-columns: minmax(96px, 0.72fr) minmax(0, 1.28fr);
    gap: 9px;
  }

  .workflow-card,
  .detail-block {
    padding: 14px;
  }

  .status-toggle {
    flex-direction: column;
  }

  .status-toggle ::v-deep .v-btn {
    flex: 0 0 42px;
    justify-content: flex-start;
    width: 100%;
  }

  .attachment-row {
    flex-wrap: wrap;
  }

  .attachment-copy {
    max-width: calc(100% - 48px);
  }

  .attachment-actions {
    justify-content: flex-end;
    width: 100%;
    padding-top: 3px;
    border-top: 1px solid rgba(255, 255, 255, 0.075);
  }

  .history-header {
    flex-wrap: wrap;
  }

  .history-count {
    margin-left: 40px;
  }

  .comment-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .comment-submit {
    width: 100%;
  }
}

@media (max-width: 379px) {
  .hero-copy p {
    display: none;
  }

  .hero-content {
    gap: 10px;
  }

  .overview-card {
    min-height: 104px;
    padding: 9px 8px;
  }

  .overview-label {
    font-size: 8px;
  }

  .form-dialog-actions {
    flex-direction: column-reverse;
  }

  .form-dialog-actions .secondary-action,
  .form-dialog-actions .primary-dialog-action {
    flex: 0 0 42px;
    width: 100%;
  }

  .detail-dialog-actions .delete-action,
  .detail-dialog-actions .pdf-action,
  .detail-dialog-actions .primary-dialog-action {
    flex: 1 1 calc(50% - 4px);
  }
}
</style>
